import { cn } from '../../../lib/utils'
import { RecurringShift } from '@shiftly/shared'
import { formatTime } from '../../../utils/format'
import { useRef } from 'react'
import { BaseCalendarView } from './base-calendar-view'
import { DateTime } from 'luxon'

interface SplitWeekRecurringShiftCalendarViewProps {
  events: RecurringShift[]
  className?: string
  onEventClick?: (event: RecurringShift) => void
  showToolbar?: boolean
  minHeight?: number
  getModelName?: (model_id: string) => string
  getUserName?: (user_id: string) => string
  allowNavigation?: boolean
  onEventEdit?: (event: RecurringShift) => void
  onEventDelete?: (event: RecurringShift) => void
  onMultiEventEdit?: (events: RecurringShift[]) => void
  onMultiEventDelete?: (events: RecurringShift[]) => void
  permissions?: string[]
}

export function SplitWeekRecurringShiftCalendarView({
  events,
  className,
  onEventClick,
  showToolbar = true,
  getModelName,
  getUserName,
  allowNavigation = true,
  onEventEdit,
  onEventDelete,
  onMultiEventEdit,
  onMultiEventDelete,
  permissions
}: SplitWeekRecurringShiftCalendarViewProps) {
  const weekdayCalendarRef = useRef<any>(null)
  const weekendCalendarRef = useRef<any>(null)
  const isUpdating = useRef(false)

  const adaptRecurringShiftToCalendarEvent = (shift: RecurringShift) => {
    const today = DateTime.utc();
    const currentWeekday = today.weekday;
    const monday = today.minus({ days: currentWeekday - 1 }).startOf('day');

    const targetWeekday = shift.day_of_week === 5 ? 6 : shift.day_of_week === 6 ? 7 : shift.day_of_week + 1;
    const shiftDate = monday.plus({ days: targetWeekday - 1 });

    const [startHour, startMinute] = shift.start_time.split(':').map(Number);
    const [endHour, endMinute] = shift.end_time.split(':').map(Number);

    let startDate = shiftDate.set({ hour: startHour, minute: startMinute, second: 0, millisecond: 0 });
    let endDate = shiftDate.set({ hour: endHour, minute: endMinute, second: 0, millisecond: 0 });

    if (endDate <= startDate) {
      endDate = endDate.plus({ days: 1 });
    }

    return {
      ...shift,
      start: startDate.toJSDate(),
      end: endDate.toJSDate(),
      title: '',
      start_time_value: shift.start_time,
      end_time_value: shift.end_time,
      allDay: false
    };
  };

  const renderEventContent = (eventInfo: { event: any }) => {
    const shift = eventInfo.event.extendedProps;
    return (
      <div className="event-content">
        <div className="flex flex-col gap-0.5 py-2 px-2">
          <div className="font-medium truncate">
            {getModelName?.(shift.model_id) || 'Unknown Model'}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(shift.start_time_value)} - {formatTime(shift.end_time_value)}
          </div>
          <div className="text-xs truncate">
            {shift.shift_code || 'Unknown Shift Code'}
            <span className="text-muted-foreground ml-1">
              ({shift.user_id && getUserName ? getUserName(shift.user_id) : 'Unassigned'})
            </span>
          </div>
        </div>
      </div>
    );
  };

  const getEventClassNames = (_: { event: any }) => {
    return 'gray-shift';
  }

  const weekdayToolbar = {
    left: allowNavigation ? 'prev,next today' : '',
    center: 'title',
    right: ''
  }

  const weekendToolbar = {
    left: '',
    center: 'title',
    right: ''
  }

  const handleWeekdayDatesSet = (arg: { start: DateTime, end: DateTime, view: any }) => {
    if (isUpdating.current) return;
    isUpdating.current = true;

    requestAnimationFrame(() => {
      if (weekendCalendarRef.current) {
        const api = weekendCalendarRef.current.getApi();
        api.gotoDate(arg.start.toJSDate());
      }

      isUpdating.current = false;
    });
  };

  return (
    <div className={cn("grid grid-cols-[1fr,2fr]", className)}>
      <BaseCalendarView
        calendarRef={weekdayCalendarRef}
        events={events.map(adaptRecurringShiftToCalendarEvent)}
        view="timeGridWeek"
        showToolbar={showToolbar}
        toolbar={weekdayToolbar}
        onEventClick={onEventClick ?
          (eventInfo) => onEventClick(eventInfo.event.extendedProps)
          : undefined}
        onEventEdit={onEventEdit}
        onEventDelete={onEventDelete}
        onMultiEventEdit={onMultiEventEdit}
        onMultiEventDelete={onMultiEventDelete}
        permissions={permissions}
        renderEventContent={renderEventContent}
        eventClassNames={getEventClassNames}
        className="weekday-calendar pr-0"
        hiddenDays={[0, 2, 3, 4, 5, 6]}
        slotLabelInterval="08:00:00"
        slotLabelFormat={
          (arg) => {
            const hour = arg.date.marker.getUTCHours();
            if (hour === 6) return '6-2';
            if (hour === 14) return '2-10';
            return '10-6';
          }
        }
        onDatesSet={handleWeekdayDatesSet}
        showDateNumbers={false}
        nowIndicator={false}
        titleFormat={() => 'Mon-Fri'}
      />
      <BaseCalendarView
        calendarRef={weekendCalendarRef}
        events={events.map(adaptRecurringShiftToCalendarEvent)}
        view="timeGridWeek"
        showToolbar={showToolbar}
        toolbar={weekendToolbar}
        onEventClick={onEventClick ?
          (eventInfo) => onEventClick(eventInfo.event.extendedProps)
          : undefined}
        onEventEdit={onEventEdit}
        onEventDelete={onEventDelete}
        onMultiEventEdit={onMultiEventEdit}
        onMultiEventDelete={onMultiEventDelete}
        permissions={permissions}
        renderEventContent={renderEventContent}
        eventClassNames={getEventClassNames}
        className="weekend-calendar pl-0"
        hiddenDays={[1, 2, 3, 4, 5]}
        slotLabelInterval="12:00:00"
        slotLabelFormat={
          (arg) => {
            const hour = arg.date.marker.getUTCHours();
            if (hour === 6) return '6-6';
            if (hour === 18) return '6-6';
            return '';
          }
        }
        showDateNumbers={false}
        nowIndicator={false}
      />
    </div>
  )
} 